import { createContext, useCallback, useContext, useState } from 'react';

import {
  SalesforceConnection,
  MetadataSyncResult,
} from '@modules/salesforce/models';
import { MetadataConnectionModal } from '@modules/salesforce/metadata-connections/modals/connection-modal';
import { RefreshMetadataModal } from '@modules/salesforce/metadata-connections/modals/refresh-metadata-modal';
import { UserConnectionModal } from '@modules/salesforce/user-connections/modals/connection-modal';
import { DeleteConnectionModal } from '@modules/salesforce/user-connections/modals/delete-connection-modal';
import { Application } from '@models/application.model';
import { Project } from '@models/project.model';
import { Application } from '@models/application.model';
import { Project } from '@models/project.model';
/**
 * Different Screens Displayed in the Metadata-Connection Modal
 */
enum MetadataConnectionModalScreens {
  // CRUD
  CONNECTION_CREATE_SCREEN = 'METADATA_CONNECTION_CREATE_SCREEN',
  CONNECTION_AUTH_FAILURE_SCREEN = 'METADATA_CONNECTION_AUTH_FAILURE_SCREEN',
  CONNECTION_DETAILS_SCREEN = 'METADATA_CONNECTION_DETAILS_SCREEN',
  CONNECTION_EDIT_SCREEN = 'METADATA_CONNECTION_EDIT_SCREEN',
  CONNECTION_UPDATE_SUCCESS_SCREEN = 'METADATA_CONNECTION_UPDATE_SUCCESS_SCREEN',
  CONNECTION_SELECT_SCREEN = 'METADA_CONNECTION_SELECT_SCREEN',

  // SYNC
  METADATA_SYNC_START_SCREEN = 'METADATA_SYNC_START_SCREEN',
  METADATA_SYNC_POLLING_SCREEN = 'METADATA_SYNC_POLLING_SCREEN',
  METADATA_SYNC_FAILURE_SCREEN = 'METADATA_SYNC_FAILURE_SCREEN',
  METADATA_SYNC_SUCCESSFUL_SCREEN = 'METADATA_SYNC_SUCCESSFUL_SCREEN',
  METADATA_SYNC_DUPLICATE_SCREEN = 'METADATA_SYNC_DUPLICATE_SCREEN',
  METADATA_SYNC_DATA_PREPARATION_SCREEN = 'METADATA_SYNC_DATA_PREPARATION_SCREEN',
  METADATA_TEST_SUCCESSFUL = 'METADATA_TEST_SYNC_SUCCESSFUL_SCREEN',
  METADATA_SYNC_PARTIALLY_SUCCESSFUL_SCREEN = 'METADATA_SYNC_PARTIALLY_SUCCESSFUL_SCREEN',
}

/**
 * Different Screens Displayed in the User-Connection Modal
 */
enum UserConnectionModalScreens {
  CONNECTION_CREATE_SCREEN = 'USER_CONNECTION_CREATE_SCREEN',
  CONNECTION_AUTH_FAILURE_SCREEN = 'USER_CONNECTION_AUTH_FAILURE_SCREEN', // banner note
  CONNECTION_DETAILS_SCREEN = 'USER_CONNECTION_DETAILS_SCREEN',
  CONNECTION_EDIT_SCREEN = 'USER_CONNECTION_EDIT_SCREEN',
  CONNECTION_SUCCESSFUL_SCREEN = 'USER_CONNECTION_SUCCESSFUL_SCREEN',
  CONNECTION_TEST_SUCCESSFUL = 'CONNECTION_TEST_SYNC_SUCCESSFUL_SCREEN',

  CONNECTION_FAILURE_SCREEN = 'USER_CONNECTION_FAILURE_SCREEN',
  CONNECTION_UPDATE_SUCCESS_SCREEN = 'USER_CONNECTION_UPDATE_SUCCESS_SCREEN',
}

/**
 * Different Screens Displayed in the Refresh Modal
 */
enum RefreshModalScreens {
  SYNC_TRIGGERED = 'SYN_TRIGGERED',
  SYNC_ALREADY_TRIGGERED = 'SYNC_ALREADY_TRIGGERED',
  SYNC_PARTIALLY_TRIGGERED = 'SYNC_PARTIALLY_TRIGGERED',
}

/**
 * Different Types of Modals That Appear on the Salesforce Connections Screen
 */
type OverlayMode =
  | 'none'
  | 'metadata_connection'
  | 'user_connection'
  | 'refresh_metadata_screen';

type MetadataConnectionContextProps = {
  metadataConnection: SalesforceConnection | null;
  handleMetadataConnection(metadataConnection: SalesforceConnection): void;
  hasMetadataConnection: boolean;
  initialSyncResult: MetadataSyncResult | null;
  handleMetadataSyncResult(syncResult: MetadataSyncResult): void;
  hasSuccessfulSyncResult: boolean;
  loading: boolean;
  handleLoading(loading: boolean): void;
  reload: number;
  handleReload(): void;
  selectedApplication(application: Application);
  handleUpdateSelectedApplication(application: Application): void;
  selectedProject(project: Project);
  handleUpdateSelectedProject(project: Project): void;
};

/**
 * Extends SalesforceConnection by adding reload and hasFailed properties, which help to avoid maintaining two separate states for the Salesforce connection.
 */
class SelectedSalesForceConnection extends SalesforceConnection {
  /**
   * Determines if a reload is needed for the connection list. Metadata and user connections must be refreshed when actions such as create, edit, and delete are performed.
   */
  reload: boolean;
  /**
   	Determines if the metadata connection failed during creation or initial sync. One successful metadata connection is necessary for Salesforce to function, so cleanup is performed in case of failure. This does not affect user connections.
   */
  hasFailed: boolean;
}

type SFContextProps = MetadataConnectionContextProps & {
  /**
   * Keeps track of which dialog to display
   */
  overlayMode: OverlayMode;
  /**
   * A handler method for updating overlayMode.
   */
  handleUpdateOverlayMode(value: OverlayMode): void;

  /**
   * Tracks which UI should be displayed in the MetadataConnection model.
   */
  metadataConnectionModalScreen: MetadataConnectionModalScreens;
  /**
   * A handler method for updating the metadataConnectionModalScreen value.
  handleUpdateMetadataConnectionModalScreen(
    value: MetadataConnectionModalScreens,
  ): void;

  /**
   *  Tracks which UI should be displayed in the UserConnection model.
   */
  userConnectionModalScreen: UserConnectionModalScreens;
  /**
   *  A handler method for updating the userConnectionModalScreen value.
   */
  handleUpdateUserConnectionModalScreen(
    value: UserConnectionModalScreens,
  ): void;

  /**
   * Tracks which UI should be displayed in the Refresh model.
   */
  refreshModalScreen: RefreshModalScreens;
  /**
   * A handler method for updating the refreshModalScreen value.
   */
  handleUpdateRefreshModalScreen(value: RefreshModalScreens): void;

  /**
   * 	Stores the value for the newly created or edited connection. This is necessary because the connection is shared across multiple screens of connection modals.
   */
  selectedConnection: SelectedSalesForceConnection;
  /**
   * A handler method for updating the selectedConnection value.
   */
  handleUpdateSelectedConnection(connection: SalesforceConnection): void;

  /**
   * Tracks the visibility status of the delete modal.
   */
  showDeleteModal: boolean;
  /**
   *  A handler method to set showDeleteModal to true.
   */
  handleShowDeleteModal(): void;
  /**
   *  A handler method to set showDeleteModal to false.
   */
  handleHideDeleteModal(): void;

  /**
   	Updates the reload flag value, which triggers useEffect in useSalesforceConnectionInitSetup to refresh the Salesforce connection page.
   */
  handleReload(): void;

  refetchConnectionList: boolean;

  handleRefetchConnectionList(refetchConnectionList: boolean): void;

  duplicateConnectionName: string;
  handleDuplicateConnectionName(connectionName: string): void;
  /**
   * Stores the error object from the salesforce connection API call.
   */
  connectionError: string;
  /**
   * A handler method for updating the connectionError value.
   */
  handleConnectionError(error: string): void;

  handleUpdateMetadataConnectionModalScreen(
    value: MetadataConnectionModalScreens,
  ): void;
};

const SFConnectionContext = createContext<SFContextProps | undefined>(
  undefined,
);

/**
 * Provider for handling states that are common between various metadata-connection and user-connection modals.
 */
const SFConnectionProvider = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [reload, setReload] = useState<number>(Date.now());
  const [duplicateConnectionName, setDuplicateConnectionName] = useState('');
  const [metadataConnection, setMetadataConnection] =
    useState<SalesforceConnection | null>(null);
  const [hasMetadataConnection, setHasMetadataConnection] = useState(false);
  const [hasSuccessfulSync, setHasSuccessfulSync] = useState(false);
  const [metadataSyncResult, setMetadataSyncResult] =
    useState<MetadataSyncResult | null>(null);

  const [overlayMode, setOverlayMode] = useState<OverlayMode>('none');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [refetchConnectionList, setRefetchConnectionList] =
    useState<boolean>(false);
  const [metadataConnectionModalScreen, setMetadataConnectionModalScreen] =
    useState<MetadataConnectionModalScreens>(
      MetadataConnectionModalScreens.CONNECTION_CREATE_SCREEN,
    );

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const [userConnectionModalScreen, setUserConnectionModalScreen] =
    useState<UserConnectionModalScreens>(
      UserConnectionModalScreens.CONNECTION_CREATE_SCREEN,
    );

  const [refreshModalScreen, setRefreshModalScreen] =
    useState<RefreshModalScreens>(RefreshModalScreens.SYNC_TRIGGERED);

  const [selectedConnection, setSelectedConnection] =
    useState<SelectedSalesForceConnection | null>(null);

  const [connectionError, setConnectionError] = useState<string>(null);
  // handle metadata connection  initial setup
  const handleLoading = useCallback((loadingValue: boolean) => {
    setLoading(loadingValue);
  }, []);

  const handleRefetchConnectionList = useCallback(
    (refetchConnectionList: boolean) => {
      setRefetchConnectionList(refetchConnectionList);
    },
    [],
  );

  const handleDuplicateConnectionName = useCallback(
    (connectionName: string) => {
      setDuplicateConnectionName(connectionName);
    },
    [],
  );

  const handleMetadataSyncResult = useCallback(
    (syncResultValue: MetadataSyncResult) => {
      setMetadataSyncResult(syncResultValue);
      const isSuccessfulSync = syncResultValue == MetadataSyncResult.SUCCESS;
      setHasSuccessfulSync(isSuccessfulSync);
    },
    [],
  );

  const handleMetadataConnection = useCallback(
    (connectionValue: SalesforceConnection) => {
      setMetadataConnection(connectionValue);
      setSelectedConnection(connectionValue as SelectedSalesForceConnection);
      setHasMetadataConnection(!!connectionValue);
    },
    [],
  );

  const handleUpdateSelectedApplication = useCallback(
    (application: Application) => {
      setSelectedApplication(application);
    },
    [],
  );

  const handleUpdateSelectedProject = useCallback((project: Project) => {
    setSelectedProject(project);
  }, []);

  const handleReload = () => {
    setReload(Date.now());
  };

  // update overlay mode
  const handleUpdateOverlayMode = useCallback((value: OverlayMode) => {
    setOverlayMode(value);
  }, []);

  // update metadataConnection screen
  const handleUpdateMetadataConnectionModalScreen = useCallback(
    (value: MetadataConnectionModalScreens) => {
      setMetadataConnectionModalScreen(value);
    },
    [],
  );

  // update userConnection screen
  const handleUpdateUserConnectionModalScreen = useCallback(
    (value: UserConnectionModalScreens) => {
      setUserConnectionModalScreen(value);
    },
    [],
  );

  // update refresh modal screen
  const handleUpdateRefreshModalScreen = useCallback(
    (value: RefreshModalScreens) => {
      setRefreshModalScreen(value);
    },
    [],
  );

  // update selectedConnection
  const handleUpdateSelectedConnection = useCallback(
    (connection: SalesforceConnection) => {
      setSelectedConnection(connection as SelectedSalesForceConnection);
    },
    [],
  );

  // update delete modal visibility
  const handleShowDeleteModal = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleHideDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConnectionError = useCallback((error: string) => {
    setConnectionError(error);
  }, []);
  return (
    <SFConnectionContext.Provider
      value={{
        metadataConnection,
        handleMetadataConnection,
        hasMetadataConnection,
        initialSyncResult: metadataSyncResult,
        handleMetadataSyncResult,
        hasSuccessfulSyncResult: hasSuccessfulSync,
        loading,
        handleLoading,
        reload,
        handleReload,

        overlayMode,
        handleUpdateOverlayMode,

        metadataConnectionModalScreen,
        handleUpdateMetadataConnectionModalScreen,

        userConnectionModalScreen,
        handleUpdateUserConnectionModalScreen,

        refreshModalScreen,
        handleUpdateRefreshModalScreen,

        selectedConnection,
        handleUpdateSelectedConnection,

        showDeleteModal,
        handleShowDeleteModal,
        handleHideDeleteModal,

        selectedApplication,
        handleUpdateSelectedApplication,

        selectedProject,
        handleUpdateSelectedProject,

        refetchConnectionList,
        handleRefetchConnectionList,

        duplicateConnectionName,
        handleDuplicateConnectionName,
        connectionError,
        handleConnectionError,
      }}
    >
      {children}

      <MetadataConnectionModal />

      <UserConnectionModal />

      <DeleteConnectionModal />

      <RefreshMetadataModal />
    </SFConnectionContext.Provider>
  );
};

/**
 * Utility method to retrieve states from SFConnectionProvider, eliminating the need for multiple imports of useContext and SFConnectionContext
 */
const useSFConnectionState = () => {
  const context = useContext(SFConnectionContext);

  if (!context) {
    throw new Error(
      'useSFConnectionState must be used within a SFConnectionProvider',
    );
  }

  return context;
};

export {
  SelectedSalesForceConnection,
  MetadataConnectionModalScreens,
  UserConnectionModalScreens,
  RefreshModalScreens,
  SFConnectionProvider,
  useSFConnectionState,
};
